<script setup>
/* global axios */
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const whatsappAppId = ref('');
const whatsappAppSecret = ref('');
const whatsappConfigurationId = ref('');
const apiVersion = ref('');
const isLoading = ref(true);
const isSaving = ref(false);
const hasExistingConfig = ref(false);

const cloudInboxes = ref([]);
const isLoadingInboxes = ref(true);

const accountId = computed(() => route.params.accountId);

const fetchConfig = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/whatsapp_config`
    );
    whatsappAppId.value = data.whatsapp_app_id || '';
    whatsappConfigurationId.value = data.whatsapp_configuration_id || '';
    apiVersion.value = data.whatsapp_api_version || 'v25.0';
    hasExistingConfig.value = Boolean(data.whatsapp_app_id);
  } catch {
    // no config yet
  }
};

const fetchInboxes = async () => {
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/whatsapp_config/inboxes`
    );
    cloudInboxes.value = data.inboxes || [];
  } catch {
    cloudInboxes.value = [];
  } finally {
    isLoadingInboxes.value = false;
  }
};

const saveConfig = async () => {
  if (
    !whatsappAppId.value ||
    (!whatsappAppSecret.value && !hasExistingConfig.value)
  ) {
    useAlert(t('WHATSAPP_CONFIG.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  try {
    isSaving.value = true;
    const payload = {
      whatsapp_app_id: whatsappAppId.value,
      whatsapp_configuration_id: whatsappConfigurationId.value,
    };
    if (whatsappAppSecret.value)
      payload.whatsapp_app_secret = whatsappAppSecret.value;
    await axios.put(
      `/api/v1/accounts/${accountId.value}/whatsapp_config`,
      payload
    );
    hasExistingConfig.value = true;
    whatsappAppSecret.value = '';
    useAlert(t('WHATSAPP_CONFIG.SAVE_SUCCESS'));
  } catch {
    useAlert(t('WHATSAPP_CONFIG.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openInbox = inboxId => {
  router.push(accountScopedRoute('settings_inbox_show', { inboxId }));
};

const createNewInbox = () => {
  router.push(
    accountScopedRoute('settings_inboxes_page_channel', {
      page: 'new',
      sub_page: 'whatsapp',
    })
  );
};

onMounted(async () => {
  isLoading.value = true;
  await Promise.all([fetchConfig(), fetchInboxes()]);
  isLoading.value = false;
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="overflow-auto p-6">
    <BaseSettingsHeader
      :title="t('WHATSAPP_CONFIG.TITLE')"
      description="Gerencie credenciais Meta e monitore as inboxes conectadas à API oficial. Para criar uma nova inbox, use o botão abaixo."
    />

    <div v-if="isLoading" class="flex items-center justify-center py-16">
      <Spinner :size="28" class="text-n-slate-10" />
    </div>

    <div v-else class="max-w-[900px] flex flex-col gap-6">
      <!-- Inboxes Conectadas -->
      <div class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden">
        <div
          class="flex items-center justify-between px-5 py-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-sm font-semibold text-n-slate-12">
              Inboxes Cloud API
            </h2>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{ cloudInboxes.length }}
              {{
                cloudInboxes.length === 1
                  ? 'inbox conectada'
                  : 'inboxes conectadas'
              }}
              usando WhatsApp oficial da Meta
            </p>
          </div>
          <button
            class="px-3 py-2 text-xs font-medium rounded-lg bg-n-brand text-white hover:opacity-90 flex items-center gap-2"
            @click="createNewInbox"
          >
            <span class="i-lucide-plus size-3.5" />
            Criar nova inbox
          </button>
        </div>

        <div
          v-if="isLoadingInboxes"
          class="flex items-center justify-center py-10"
        >
          <Spinner :size="20" class="text-n-slate-10" />
        </div>

        <div
          v-else-if="!cloudInboxes.length"
          class="flex flex-col items-center gap-2 py-12 px-6 text-center"
        >
          <div
            class="size-12 rounded-full bg-n-alpha-2 flex items-center justify-center mb-1"
          >
            <span class="i-lucide-message-square-plus size-6 text-n-slate-9" />
          </div>
          <p class="text-sm text-n-slate-12">
            Nenhuma inbox Cloud API criada ainda
          </p>
          <p class="text-xs text-n-slate-10 max-w-xs">
            Configure as credenciais da Meta abaixo e depois crie uma inbox via
            Embedded Signup ou configuração manual.
          </p>
        </div>

        <table v-else class="w-full">
          <thead>
            <tr class="border-b border-n-weak bg-n-alpha-1">
              <th
                class="text-left text-xs font-semibold text-n-slate-10 px-5 py-2.5"
              >
                Nome
              </th>
              <th
                class="text-left text-xs font-semibold text-n-slate-10 px-5 py-2.5"
              >
                Número
              </th>
              <th
                class="text-left text-xs font-semibold text-n-slate-10 px-5 py-2.5"
              >
                Credenciais
              </th>
              <th
                class="text-right text-xs font-semibold text-n-slate-10 px-5 py-2.5"
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="inbox in cloudInboxes"
              :key="inbox.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 transition-colors"
            >
              <td class="px-5 py-3">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ inbox.name }}
                </p>
                <p class="text-xs text-n-slate-9 mt-0.5">
                  Inbox #{{ inbox.id }}
                </p>
              </td>
              <td class="px-5 py-3">
                <div class="flex items-center gap-1.5 text-sm text-n-slate-11">
                  <span class="i-lucide-phone size-3.5" />
                  <span>{{ inbox.phone_number || '—' }}</span>
                </div>
                <p
                  v-if="inbox.phone_number_id"
                  class="text-xs text-n-slate-9 font-mono mt-0.5"
                >
                  ID: {{ inbox.phone_number_id }}
                </p>
              </td>
              <td class="px-5 py-3">
                <span
                  v-if="inbox.api_key_present"
                  class="px-2.5 py-1 text-xs rounded-full font-medium bg-green-500/10 text-green-500"
                >
                  <span
                    class="i-lucide-check-circle size-3 inline-block align-middle"
                  />
                  Token ativo
                </span>
                <span
                  v-else
                  class="px-2.5 py-1 text-xs rounded-full font-medium bg-amber-500/10 text-amber-500"
                >
                  <span
                    class="i-lucide-alert-triangle size-3 inline-block align-middle"
                  />
                  Sem token
                </span>
              </td>
              <td class="px-5 py-3 text-right">
                <button
                  class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-2 inline-flex items-center gap-1.5"
                  @click="openInbox(inbox.id)"
                >
                  <span class="i-lucide-settings size-3.5" />
                  Abrir inbox
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Credenciais -->
      <SectionLayout
        :title="t('WHATSAPP_CONFIG.CREDENTIALS.TITLE')"
        :description="t('WHATSAPP_CONFIG.CREDENTIALS.DESCRIPTION')"
      >
        <div class="flex flex-col gap-4">
          <WithLabel :label="t('WHATSAPP_CONFIG.CREDENTIALS.APP_ID')">
            <NextInput
              v-model="whatsappAppId"
              :placeholder="t('WHATSAPP_CONFIG.CREDENTIALS.APP_ID_PLACEHOLDER')"
            />
          </WithLabel>

          <WithLabel :label="t('WHATSAPP_CONFIG.CREDENTIALS.APP_SECRET')">
            <NextInput
              v-model="whatsappAppSecret"
              type="password"
              :placeholder="
                hasExistingConfig
                  ? t('WHATSAPP_CONFIG.CREDENTIALS.SECRET_UNCHANGED')
                  : t('WHATSAPP_CONFIG.CREDENTIALS.APP_SECRET_PLACEHOLDER')
              "
            />
          </WithLabel>

          <WithLabel :label="t('WHATSAPP_CONFIG.CREDENTIALS.CONFIG_ID')">
            <NextInput
              v-model="whatsappConfigurationId"
              :placeholder="
                t('WHATSAPP_CONFIG.CREDENTIALS.CONFIG_ID_PLACEHOLDER')
              "
            />
          </WithLabel>

          <WithLabel :label="t('WHATSAPP_CONFIG.CREDENTIALS.API_VERSION')">
            <NextInput v-model="apiVersion" disabled />
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('WHATSAPP_CONFIG.CREDENTIALS.API_VERSION_NOTE') }}
            </p>
          </WithLabel>
        </div>

        <div class="flex mt-6">
          <NextButton
            :disabled="isSaving"
            :is-loading="isSaving"
            @click="saveConfig"
          >
            {{ t('WHATSAPP_CONFIG.SAVE_BUTTON') }}
          </NextButton>
        </div>
      </SectionLayout>

      <!-- Ajuda -->
      <SectionLayout
        :title="t('WHATSAPP_CONFIG.HELP.TITLE')"
        :description="t('WHATSAPP_CONFIG.HELP.DESCRIPTION')"
      >
        <ol
          class="list-decimal list-inside flex flex-col gap-2 text-sm text-n-slate-11"
        >
          <li>{{ t('WHATSAPP_CONFIG.HELP.STEP_1') }}</li>
          <li>{{ t('WHATSAPP_CONFIG.HELP.STEP_2') }}</li>
          <li>{{ t('WHATSAPP_CONFIG.HELP.STEP_3') }}</li>
          <li>{{ t('WHATSAPP_CONFIG.HELP.STEP_4') }}</li>
        </ol>
      </SectionLayout>
    </div>
  </div>
</template>
